import React, { useContext } from 'react';
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import GroupReportContext from '../../../../Context/GroupReportContext';
import { getMindsetSum } from '../../../../utils/helper';

export default function MindsetResult2({ apiData, pageNumber, reportType }) {
  const { getPercentileFromScore, averagePercentagesMindset } = useContext(GroupReportContext);

  const is360Assessment = apiData?.memberResults?.allMembersData && Object.keys(apiData.memberResults.allMembersData).length > 0;

  let selfPercentage = 0;
  let three60Percentage = 0;

  if (!apiData || !Array.isArray(apiData.variables)) {
    return <div>No data available</div>;
  }

  const selfTotalScore = getMindsetSum(apiData.variables);
  selfPercentage = getPercentileFromScore(selfTotalScore);

  if (is360Assessment) {
    // The 360 figure is the observers' average of each observer's normalized
    // score (the respondent is excluded), as the report API and the PDF
    // calculate it. Prefer the value hydrated from the API; otherwise apply
    // the same rule to the member variables.
    if (typeof averagePercentagesMindset === 'number' && averagePercentagesMindset > 0) {
      three60Percentage = averagePercentagesMindset;
    } else {
      const observerPercentages = [];
      Object.values(apiData.memberResults.allMembersData).forEach((member) => {
        if (!member?.variables || ["360 Assessment", "Self-Assessment"].includes(member?.designation)) return;
        const mindsetSum = getMindsetSum(member.variables);
        if (mindsetSum > 0) {
          observerPercentages.push(Number(getPercentileFromScore(mindsetSum)) || 0);
        }
      });
      if (observerPercentages.length > 0) {
        three60Percentage = observerPercentages.reduce((acc, percentage) => acc + percentage, 0) / observerPercentages.length;
      }
    }
  }

  // Dynamic content for each mindset type
  const mindsetContent = {
    growth: {
      title: 'Your Results: Adaptive & Transformative',
      paragraphs: [
        `Your results indicate that you embody a <b>growth mindset</b>, aligning with the Adaptive Learner and Transformative Growth levels. This mindset reflects your belief that abilities and intelligence can be developed through effort, learning, and persistence. It's a powerful perspective that turns challenges into opportunities and views setbacks as essential steps toward mastery.`,
        `With a growth mindset, you actively embrace challenges and seek out opportunities to improve, welcoming feedback as a valuable tool for growth. You recognize effort as a pathway to progress and are not discouraged by setbacks, using them as lessons to refine your approach and build resilience.`,
        `This mindset also positively impacts those around you. By valuing collaboration and viewing others' successes as sources of inspiration rather than competition, you foster supportive and innovative environments. Your ability to integrate learning and persistence contributes to stronger relationships, teamwork, and leadership.`,
        `Your commitment to continuous improvement sets the foundation for personal and professional success. By maintaining this perspective, you are well-equipped to navigate challenges, achieve meaningful growth, and inspire others to do the same. Keep leveraging this mindset to unlock even greater potential in yourself and those you influence.`
      ]
    },
    conditional: {
      title: 'Your Results: Conditional Growth',
      paragraphs: [
        `Your results show that you align with a <b>mixed mindset</b>, a mindset that combines elements of both fixed and growth perspectives. This means you believe growth is possible but often within areas where you feel naturally talented or confident. You value effort, though it's often tied to achieving success rather than the process of growth itself.`,
        `At this stage, you may prefer challenges that match your strengths and feel comfortable with feedback that reinforces what you already know. While failure doesn't feel personal, you might hesitate to take risks that lead to uncertain outcomes.`,
        `To grow further, try viewing effort as a key part of learning, not just a path to success. Take small steps to embrace challenges outside your comfort zone and seek feedback that helps you stretch and improve. Your current mindset provides a strong starting point—by focusing on progress over perfection, you can build resilience and unlock greater opportunities for growth and success.`,
        `By shifting your perspective and celebrating growth as a journey, you can start to explore new possibilities beyond your natural strengths. This mindset shift will not only enhance your personal development but also positively impact your relationships, leadership, and collaboration with others. Growth isn't just about ability—it's about discovering your potential through persistence, adaptability, and a willingness to take on new challenges.`
      ]
    },
    fixed: {
      title: 'Your Results: Rigid & Cautious',
      paragraphs: [
        'Your results indicate that you may currently align with aspects of a <b>fixed mindset</b>, particularly within the Rigid Self-Definition and Cautious Openness levels. This mindset suggests a belief that abilities and intelligence are relatively static, which can sometimes limit risk-taking and openness to challenges. Recognizing this mindset is an important step toward exploring new possibilities and fostering growth.',
        'With a fixed mindset, you might prioritize avoiding challenges to maintain comfort or protect your self-image. Setbacks may feel discouraging, but they also present opportunities for reflection and growth. Shifting your perspective to see difficult tasks as chances to develop new skills can help reframe failure as part of the learning process rather than a limitation.',
        'Taking small, intentional steps can create momentum for change. Gradually embracing challenges, seeking feedback, and celebrating progress—no matter how small—can help you move beyond the limitations of a fixed mindset. These efforts encourage a more flexible and adaptive approach to personal and professional growth.',
        'This shift not only benefits your own development but also strengthens your relationships and interactions. By viewing effort as valuable and feedback as a tool for improvement, you can begin to foster resilience and confidence in both yourself and others.',
        'Remember, growth is a journey, not a destination. By making intentional changes, you can build a mindset that helps you unlock potential, strengthen connections, and achieve meaningful progress in all areas of your life.'
      ]
    }
  };

  // Function to determine which content to show based on percentage
  const getSelectedContentByPercentage = (percentage) => {
    if (percentage >= 0 && percentage < 16) {
      return mindsetContent.fixed;
    } else if (percentage >= 16 && percentage < 36) {
      return mindsetContent.fixed;
    } else if (percentage >= 36 && percentage < 66) {
      return mindsetContent.conditional;
    } else if (percentage >= 66 && percentage < 86) {
      return mindsetContent.growth;
    } else if (percentage >= 86 && percentage <= 100) {
      return mindsetContent.growth;
    } else {
      return mindsetContent.growth;
    }
  };

  // Get selected content for both SELF and 360
  const selfSelectedContent = getSelectedContentByPercentage(Math.round(selfPercentage));
  const three60SelectedContent = getSelectedContentByPercentage(Math.round(three60Percentage));

  // console.log('MindsetResult2 Debug - SELF percentage:', selfPercentage, 'selected content:', selfSelectedContent.title);
  // console.log('MindsetResult2 Debug - 360 percentage:', three60Percentage, 'selected content:', three60SelectedContent.title);

  return (
    <div style={{ backgroundColor: "rgb(255, 255, 255)" }}>
      <HeaderPreview apiData={apiData} />
      <div style={{ padding: '18px 0', margin: 'auto', overflowY: 'auto', marginBottom: '6%', fontFamily: 'Helvetica', color: '#000' }} >

        {/* 360 Results Section */}
        {is360Assessment && (
          <>
            <h2 style={{ fontSize: '10px', lineHeight: '14px', letterSpacing: 0, fontWeight: 700, color: '#000', textTransform: 'uppercase', fontFamily: 'Helvetica', margin: 0 }} >
              {three60SelectedContent.title}
            </h2>

            {three60SelectedContent.paragraphs.map((paragraph, index) => (
              <p key={index} style={{ color: '#000', fontSize: '10px', fontWeight: 400, fontFamily: 'Helvetica', lineHeight: '14px', letterSpacing: 0, marginTop: "6px" }} 
               dangerouslySetInnerHTML={{ __html: paragraph }}
              />

                
            ))}
          </>
        )}
      </div>
      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  );
}